/**
 * Consolidated Jupiter API hooks for swap functionality
 */
// ============================================================================
// Imports
// ============================================================================

import { notifyTransaction, type TransactionObserver } from "@shared/types/transactionObserver";
import {
    INITIAL_TRANSACTION_STATE,
    TransactionError,
    type TransactionState,
    useTransactionSigner,
    useWallet
} from "@solomon-apps/shared";
import type {
    JupiterHoldingsDetailedResponse,
    JupiterSwapExecuteResponse,
    JupiterTokenSearchResult
} from "@solomon-labs/swap-client";
import { keepPreviousData, useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { executeJupiterSwap, fetchSwapQuote, jupiterProvider } from "@/services/jupiter";

import { useDebounce } from "../useDebounce";

import type { JupiterExecuteParams, JupiterQuoteParams, JupiterUltraQuoteResponse } from "./jupiterPayload";

// ============================================================================
// Types
// ============================================================================

export interface UseJupiterExecuteReturn {
    /** Current transaction state */
    state: TransactionState;
    /** Execute the swap transaction */
    execute: (params: JupiterExecuteParams) => Promise<string | null>;
    /** Confirmed execution details for flows that spend the output in a later step. */
    executeWithReceipt: (
        params: JupiterExecuteParams,
        options?: JupiterExecutionOptions
    ) => Promise<JupiterSwapExecuteResponse | null>;
    /** Reset state to initial */
    reset: () => void;
}

export interface JupiterExecutionOptions {
    observer?: TransactionObserver;
    /** Persist recovery information after signing and before any network submission. */
    beforeSubmit?: (signedBytes: Uint8Array) => void | Promise<void>;
    /** Emit sanitized lifecycle diagnostics for a production flow under investigation. */
    diagnostics?: { flow: "usdv-migration" };
}

type JupiterExecutionStage = "validation" | "decoding" | "signing" | "persistence" | "execution";

interface JupiterDiagnosticError {
    name: string;
    message: string;
    code?: string | number;
}

function redactAddress(address: string | null | undefined): string | null {
    if (!address) return null;
    return address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-6)}` : address;
}

function describeDiagnosticError(caught: unknown): JupiterDiagnosticError {
    const record = typeof caught === "object" && caught !== null ? (caught as Record<string, unknown>) : null;
    const code = record?.code;
    return {
        name: caught instanceof Error ? caught.name : typeof record?.name === "string" ? record.name : "UnknownError",
        message:
            caught instanceof Error
                ? caught.message
                : typeof record?.message === "string"
                  ? record.message
                  : String(caught),
        ...(typeof code === "string" || typeof code === "number" ? { code } : {})
    };
}

export interface UseJupiterTokenSearchBatchResult {
    data: JupiterTokenSearchResult[];
    isFetching: boolean;
}

// ============================================================================
// useJupiterQuote - Fetch swap quotes with debouncing
// ============================================================================

const DEBOUNCE_MS = 400;
const TOKEN_SEARCH_BATCH_SIZE = 75;

/**
 * Hook to fetch Jupiter Swap V2 swap quotes with built-in debouncing.
 *
 * Features:
 * - 400ms debounce to prevent API spam on rapid input changes
 * - Automatic retry with backoff (skips retry on rate limit)
 * - Quote auto-refresh every 15 seconds while active
 * - Slippage is automatically optimized by Jupiter Swap V2
 *
 * @param params - Quote parameters (inputMint, outputMint, amount, taker)
 * @returns React Query result with quote data
 */
export function useJupiterQuote(
    params: JupiterQuoteParams | null,
    options: { debounceMs?: number } = {}
): UseQueryResult<JupiterUltraQuoteResponse, Error> {
    // Debounce params to prevent API calls on every keystroke
    const debounceMs = options.debounceMs ?? DEBOUNCE_MS;
    const debouncedParams = useDebounce(params, debounceMs);
    const queryParams = debounceMs <= 0 ? params : debouncedParams;

    return useQuery({
        queryKey: ["jupiter-quote", "swap-v2", queryParams],
        queryFn: async (): Promise<JupiterUltraQuoteResponse> => {
            return fetchSwapQuote({
                inputMint: queryParams!.inputMint,
                outputMint: queryParams!.outputMint,
                amount: queryParams!.amount,
                taker: queryParams!.taker
            });
        },
        enabled: !!queryParams && !!queryParams.inputMint && !!queryParams.outputMint && Number(queryParams.amount) > 0,
        staleTime: 5 * 1000, // Consider stale after 5 seconds
        gcTime: 30 * 1000, // Keep in cache for 30 seconds
        refetchInterval: 15 * 1000, // Auto-refresh every 15 seconds
        refetchIntervalInBackground: false, // Don't refresh when tab is hidden
        retry: (failureCount, error) => {
            // Don't retry on rate limit (429) - wait for debounce/user to slow down
            if (error instanceof Error && error.message.includes("429")) {
                return false;
            }
            return failureCount < 2;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
    });
}

// ============================================================================
// useJupiterExecute - Execute signed swap transactions
// ============================================================================

/**
 * Hook for executing Jupiter Swap V2 swaps
 *
 * Signs the transaction from Jupiter quote and submits to the execute endpoint.
 *
 * @returns Transaction state and execute function
 *
 * @example
 * ```tsx
 * const { state, execute, reset } = useJupiterExecute();
 *
 * const handleSwap = async () => {
 *   const signature = await execute(executeParams);
 *   if (signature) {
 *     console.log('Swap executed:', signature);
 *   }
 * };
 * ```
 */
export function useJupiterExecute(): UseJupiterExecuteReturn {
    const { connected, publicKey, walletName } = useWallet();
    const { signer, ready: signerReady, address: signerAddress, capabilities } = useTransactionSigner();
    const [state, setState] = useState<TransactionState>(INITIAL_TRANSACTION_STATE);
    const isExecutingRef = useRef(false);

    const executeWithReceipt = useCallback(
        async (
            params: JupiterExecuteParams,
            options?: JupiterExecutionOptions
        ): Promise<JupiterSwapExecuteResponse | null> => {
            if (isExecutingRef.current) return null;
            isExecutingRef.current = true;
            let stage: JupiterExecutionStage = "validation";
            let definiteFailure = false;
            const diagnosticsPrefix = options?.diagnostics ? `[jupiter-swap:${options.diagnostics.flow}]` : null;
            const walletAddress = publicKey?.toString() ?? null;
            const diagnosticContext = {
                requestId: params.requestId,
                walletName: walletName ?? null,
                walletAddress: redactAddress(walletAddress),
                signerAddress: redactAddress(signerAddress),
                signerReady,
                signerAddressMatchesWallet: !!walletAddress && signerAddress === walletAddress,
                capabilities: capabilities ?? null,
                inputMint: params.expectedInputMint,
                outputMint: params.expectedOutputMint,
                swapMode: params.swapMode
            };
            const logFailure = (caught: unknown): void => {
                notifyTransaction(() => options?.observer?.failed?.(caught, stage === "execution" && !definiteFailure));
                if (!diagnosticsPrefix) return;
                console.error(`${diagnosticsPrefix} failed`, {
                    ...diagnosticContext,
                    stage,
                    error: describeDiagnosticError(caught)
                });
            };

            try {
                // Validation
                if (!connected || !publicKey) {
                    const error = TransactionError.validation("WALLET_NOT_CONNECTED", "Wallet not connected");
                    logFailure(error);
                    setState({
                        ...INITIAL_TRANSACTION_STATE,
                        isError: true,
                        errorMessage: error.userMessage
                    });
                    return null;
                }

                if (!signerReady || !signer) {
                    const error = TransactionError.validation("SIGNER_NOT_READY", "Transaction signer not ready");
                    logFailure(error);
                    setState({
                        ...INITIAL_TRANSACTION_STATE,
                        isError: true,
                        errorMessage: error.userMessage
                    });
                    return null;
                }

                if (!params.requestId || !params.transaction) {
                    const error = TransactionError.validation("BUILD_FAILED", "Missing requestId or transaction");
                    logFailure(error);
                    setState({
                        ...INITIAL_TRANSACTION_STATE,
                        isError: true,
                        errorMessage: error.userMessage
                    });
                    return null;
                }

                // Start transaction
                setState({
                    ...INITIAL_TRANSACTION_STATE,
                    isLoading: true,
                    step: "building"
                });

                // Decode Base64 transaction to Uint8Array
                stage = "decoding";
                notifyTransaction(() => options?.observer?.stage?.("building"));
                const transactionBytes = Uint8Array.from(atob(params.transaction), (c) => c.charCodeAt(0));

                // Sign the transaction
                stage = "signing";
                notifyTransaction(() => options?.observer?.stage?.("awaiting_signature"));
                setState((s) => ({ ...s, step: "signing" }));
                if (diagnosticsPrefix) {
                    console.warn(`${diagnosticsPrefix} signature requested`, {
                        ...diagnosticContext,
                        transactionByteLength: transactionBytes.byteLength
                    });
                }
                const signedBytes = await signer.signTransaction(transactionBytes);

                if (!(signedBytes instanceof Uint8Array)) {
                    throw new Error("Unexpected signed transaction format");
                }
                if (diagnosticsPrefix) {
                    console.warn(`${diagnosticsPrefix} signature returned`, {
                        ...diagnosticContext,
                        transactionByteLength: transactionBytes.byteLength,
                        signedTransactionByteLength: signedBytes.byteLength,
                        bytesChanged:
                            signedBytes.byteLength !== transactionBytes.byteLength ||
                            signedBytes.some((byte, index) => byte !== transactionBytes[index])
                    });
                }

                stage = "persistence";
                notifyTransaction(() => options?.observer?.stage?.("signed"));
                await options?.beforeSubmit?.(signedBytes);
                if (diagnosticsPrefix) {
                    console.warn(`${diagnosticsPrefix} recovery record persisted`, diagnosticContext);
                }

                // Encode signed transaction back to Base64
                const signedTransaction = btoa(signedBytes.reduce((s, b) => s + String.fromCharCode(b), ""));

                // Update state to confirming (submitting to Jupiter)
                setState((s) => ({ ...s, step: "confirming" }));

                // Submit to Jupiter execute endpoint using SDK
                stage = "execution";
                notifyTransaction(() => options?.observer?.stage?.("submitting"));
                if (diagnosticsPrefix) {
                    console.warn(`${diagnosticsPrefix} execution requested`, diagnosticContext);
                }
                const result: JupiterSwapExecuteResponse = await executeJupiterSwap({
                    requestId: params.requestId,
                    signedTransaction
                });

                if (result.status === "Failed") {
                    definiteFailure = true;
                    throw new Error(result.error || "Transaction failed");
                }
                if (diagnosticsPrefix) {
                    console.warn(`${diagnosticsPrefix} execution succeeded`, {
                        ...diagnosticContext,
                        hasSignature: typeof result.signature === "string" && result.signature.length > 0
                    });
                }

                // Success
                setState({
                    isLoading: false,
                    isSuccess: true,
                    isError: false,
                    errorMessage: null,
                    signature: result.signature || null,
                    step: "complete"
                });

                return result;
            } catch (error) {
                logFailure(error);
                const transactionError = error instanceof TransactionError ? error : TransactionError.fromCaught(error);
                setState({
                    isLoading: false,
                    isSuccess: false,
                    isError: true,
                    errorMessage: transactionError.userMessage,
                    signature: null,
                    step: "idle"
                });
                return null;
            } finally {
                isExecutingRef.current = false;
            }
        },
        [capabilities, connected, publicKey, signer, signerAddress, signerReady, walletName]
    );

    const reset = useCallback(() => {
        setState(INITIAL_TRANSACTION_STATE);
    }, []);

    const execute = useCallback(
        async (params: JupiterExecuteParams): Promise<string | null> =>
            (await executeWithReceipt(params))?.signature || null,
        [executeWithReceipt]
    );

    return { state, execute, executeWithReceipt, reset };
}

// ============================================================================
// useJupiterHoldings - Fetch wallet token balances
// ============================================================================

export function useJupiterHoldings(
    walletAddress: string | null
): UseQueryResult<JupiterHoldingsDetailedResponse, Error> {
    return useQuery({
        queryKey: ["jupiter-holdings", walletAddress],
        queryFn: (): Promise<JupiterHoldingsDetailedResponse> => jupiterProvider.getHoldingsDetailed(walletAddress!),
        enabled: !!walletAddress,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 2
    });
}

// ============================================================================
// useJupiterTokenSearch - Search tokens by query
// ============================================================================

export function useJupiterTokenSearch(query: string): UseQueryResult<JupiterTokenSearchResult[], Error> {
    return useQuery({
        queryKey: ["jupiter-token-search", query],
        queryFn: (): Promise<JupiterTokenSearchResult[]> => jupiterProvider.searchTokens(query),
        enabled: query.trim().length > 0,
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: 1
    });
}

export function useJupiterTokenSearchBatch(mints: string[]): UseJupiterTokenSearchBatchResult {
    const queries: string[] = [];

    for (let i = 0; i < mints.length; i += TOKEN_SEARCH_BATCH_SIZE) {
        queries.push(mints.slice(i, i + TOKEN_SEARCH_BATCH_SIZE).join(","));
    }

    const results = useQueries({
        queries: queries.map((query) => ({
            queryKey: ["jupiter-token-search", query],
            queryFn: (): Promise<JupiterTokenSearchResult[]> => jupiterProvider.searchTokens(query),
            placeholderData: keepPreviousData,
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
            retry: 1
        }))
    });

    const metadataByMint = new Map<string, JupiterTokenSearchResult>();

    results.forEach((result) => {
        result.data?.forEach((token) => {
            if (!metadataByMint.has(token.id)) {
                metadataByMint.set(token.id, token);
            }
        });
    });

    return {
        data: Array.from(metadataByMint.values()),
        isFetching: results.some((result) => result.isFetching)
    };
}

// ============================================================================
// useJupiterVerifiedMints - Fetch verified token mints
// ============================================================================

export function useJupiterVerifiedMints(): UseQueryResult<Set<string>, Error> {
    return useQuery({
        queryKey: ["jupiter-verified-mints"],
        queryFn: (): Promise<Set<string>> => jupiterProvider.getVerifiedMints(),
        staleTime: 60 * 60 * 1000, // 1 hour
        gcTime: 6 * 60 * 60 * 1000 // 6 hours
    });
}

// ============================================================================
// Type Aliases (backwards compatibility)
// ============================================================================

export type JupiterOrderResponse = JupiterUltraQuoteResponse;
export type JupiterExecuteResponse = JupiterSwapExecuteResponse;
export {
    buildJupiterExecuteParams,
    getQuoteRejection,
    parseJupiterUltraQuotePayload,
    type JupiterExecuteParams,
    type JupiterPlatformFee,
    type JupiterQuoteParams,
    type JupiterRoutePlanStep,
    type JupiterSwapMode,
    type JupiterUltraQuoteResponse
} from "./jupiterPayload";
